import { IUserDeletionInfo } from '@account-users/account/../user/models/IDeletedUserInfo';
import { UserService } from '@account-users/account/../user/services/user.service';
import { ConfirmPackageModalComponent } from '@account-users/account/components/confirm-package-modal/confirm-package-modal.component';
import { DeleteUserModalComponent } from '@account-users/account/components/delete-user-modal/delete-user-modal.component';
import { InviteSupportUserModalComponent } from '@account-users/account/components/invite-support-user-modal/invite-support-user-modal.component';
import { InviteUserModalComponent } from '@account-users/account/components/invite-user-modal/invite-user-modal.component';
import {
  AccountCategory,
  AccountCategoryCode,
  AccountSettings,
  AccountSettingsPayload,
  AccountState,
  AccountUserType,
  DkimDomain,
  EmailQuota,
  Feature,
  IAccount,
  IAccountUser,
  LicenseCode,
  LicensePackage,
  LicensePackageCode,
  LicenseTypeConfig,
  PartnerUserInviteOptions,
  PatchSmsCreditsPayload,
  Permission,
  SmsCredits,
  StaffUserInvitation,
  StaffUserInviteOptions,
  UserInvitation,
  UserRoles,
} from '@account-users/account/models';
import { AccountService } from '@account-users/account/services/account.service';
import { DkimService } from '@account-users/account/services/dkim.service';
import { ProAccountsService } from '@account-users/account/services/pro-accounts.service';
import { SmsCreditsService } from '@account-users/account/services/sms-credits.service';
import { TrialAccountUpgradeService } from '@account-users/account/services/trial-account-upgrade.service';
import * as fromLicenses from '@account-users/account/state';
import { LoadSections } from '@account-users/account/state/account-sections/account-sections.actions';
import * as fromAccountSections from '@account-users/account/state/account-sections/account-sections.reducer';
import {
  AccountBootstrapEmailEventAction,
  CreateCrmKeyspaceAction,
  GetProAccountsAction,
  LoadAccountMigrations,
  PutProAccountsSuccessAction,
  RebootstrapLicensesAction,
  ResetUpdateLicensePackage,
  UnlinkProAccountSuccessAction,
  UpdateLicensePackage,
} from '@account-users/account/state/accounts/accounts.actions';
import { State } from '@account-users/account/state/accounts/accounts.reducer';
import { FeatureToggleStateService } from '@account-users/account/state/feature-toggles/feature-toggle-state.service';
import {
  FeatureToggleGroupNode,
  LicenseV2,
} from '@account-users/account/state/feature-toggles/models';
import * as fromAccount from '@account-users/account/state/index';
import {
  FetchAllLicenses,
  FetchLicensePackage,
  SetAllowPolling,
} from '@account-users/account/state/licenses/licenses.actions';
import { AuthService } from '@account-users/auth/auth.service';
import { UserRolesAndPermissionsService } from '@account-users/user/services/user-roles-and-permissions.service';
import * as fromPermissions from '@account-users/user/state';
import { LoadPermissions } from '@account-users/user/state/permissions/permissions.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  getAccountIntegrations,
  getIsIntegrationsLoading,
  IntegrationsService,
} from '@integrations';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import {
  ConfirmModalComponent,
  EmailEventDiscriminator,
  fileDownload,
  FormState,
  IMigration,
  RoutingState,
  Tab,
  transactionalEmailEventsList,
  updateObjectInArray,
  UpdateRecentRoutes,
} from '@shared';
import { ToastrService } from 'ngx-toastr';
import * as moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  forkJoin,
  merge,
  Observable,
  Subject,
  throwError,
} from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.scss'],
})
export class AccountDetailsComponent implements OnInit, OnDestroy {
  public readonly hasSmsCreditSubscription$ = this.store.pipe(
    select(fromLicenses.getHasFeatureLicense(Feature.SmsCreditSubscription)),
  );
  public readonly isLoadingLicensePackages$: Observable<
    boolean
  > = this.store.pipe(select(fromLicenses.getIsLoadingPackages));
  public readonly licensePackagesError$: Observable<boolean> = this.store.pipe(
    select(fromLicenses.getLicensePackagesError),
  );
  public readonly mappedProIds$: Observable<string[]> = this.store.pipe(
    select(fromAccount.getMappedProIds),
  );
  public readonly isLoadingMigrations$: Observable<boolean> = this.store.pipe(
    select(fromAccount.getIsLoadingMigrations),
  );

  public readonly getAccountSectionsTotal$: Observable<
    number
  > = this.store.pipe(select(fromAccountSections.getAccountSectionsTotal));

  public readonly licensesBootstrapState$: Observable<
    AccountState
  > = this.store.pipe(select(fromAccount.getLicensesBootstrapState));

  public readonly licensePackageUpdateState$: Observable<{
    accountPackageState: string;
    newPackage: string;
  }> = this.store.pipe(select(fromAccount.getAccountPackageState));

  public readonly integrationsLoading$ = this.store.pipe(
    select(getIsIntegrationsLoading),
  );
  public readonly integrations$ = this.store.pipe(
    select(getAccountIntegrations),
  );

  public readonly hasSupportUsers$: Subject<boolean> = new Subject<boolean>();
  public readonly hasDeactivatedUsers$: Subject<boolean> = new Subject<
    boolean
  >();
  public readonly hasMigrations$: Subject<boolean> = new Subject<boolean>();
  public readonly hasWawBackOffice$: Subject<boolean> = new Subject<boolean>();
  public readonly activeTab$ = this.route.fragment.pipe(
    map((id) => id || 'detail'),
  );
  public readonly isCrmKeyspaceCreating$ = this.store.pipe(
    select(fromAccount.getCrmKeyspaceIsCreating),
  );
  public readonly isCrmKeyspaceTimeoutFailed$ = this.store.pipe(
    select(fromAccount.getCrmKeyspaceTimeoutFailed),
  );
  public readonly rebootstrapLicensesIsLoading$ = this.store.pipe(
    select(fromAccount.getRebootstrapLicensesIsLoading),
  );
  public readonly rebootstrapLicensesTimeoutFailed$ = this.store.pipe(
    select(fromAccount.getRebootstrapLincesesTimeoutFailed),
  );

  public featureToggleGroupNode$: Observable<FeatureToggleGroupNode[]>;
  public featureTogglesLoading$: Observable<boolean>;
  public disablePackage$: Observable<boolean>;
  public licensePackages$: Observable<LicensePackage[]>;
  public accountCategories$: Observable<AccountCategory[]>;

  public tabs: Tab[] = [
    { id: 'detail', text: 'Details', show: true },
    { id: 'licenses', text: 'License packages', show: true },
    {
      id: 'feature-toggles',
      text: 'Feature toggles',
      show: true,
    },
    { id: 'account-users', text: 'Account users', show: true },
    {
      id: 'support-users',
      text: 'Staff & Partners',
      show: true,
    },
    {
      id: 'deactivated-users',
      text: 'Deactivated users',
      show: this.hasDeactivatedUsers$,
    },
    {
      id: 'proSync',
      text: 'Prosync',
      show: this.hasMigrations$,
    },
    // Waiting on Integrations Backend Support
    // { id: 'integrations', text: 'Integrations', show: true },
  ];

  public modalRef: NgbModalRef;
  public isUpdatingLicense = false;
  public errorResponse: HttpErrorResponse;
  public isLoading = true;
  public _accountDetails: IAccount;
  public isUpdatingAudiencePackageSize = false;
  public isUpdatingSectionsLimit = false;
  public isUpdatingUserSeats = false;
  public currentSmsCredits: SmsCredits = { credits: 0 };
  public proAccounts: number[];
  public readonly proAccounts$ = this.store.pipe(
    select(fromAccount.getProAccounts),
  );
  public readonly proAccountsIsLoading$ = this.store.pipe(
    select(fromAccount.getProAccountsIsLoading),
  );
  public isLoadingAccountSettings = false;
  public loadingProAccountIds: boolean;
  public AccountState = AccountState;
  public isUpdatingSmsCredits = false;
  public isLoadingSmsCredits = false;
  public isAlreadyOnAccount = false;
  public userPermissions$: Observable<Permission[]>;
  public accountCategories: AccountCategory[] = [];
  public allLicenses: LicenseV2[];
  public licenseTypes: LicenseTypeConfig[];
  public licensePackages: LicensePackage[];
  public hasOwnerRole = false;
  public migrations$: Observable<IMigration[]>;
  public lastUserLoginTime: string;
  public activeUsers$: BehaviorSubject<IAccountUser[]> = new BehaviorSubject<
    IAccountUser[]
  >([]);
  public hasStaffUserOnAccount$: Observable<boolean>;

  public cancelScheduleMigrationIsLoading$: Observable<boolean>;
  public hasOnlyTrialDispenserRole$: Observable<boolean>;
  public hasOrderManagerRole$: Observable<boolean>;
  public hasWawRole$: Observable<boolean>;
  public readonly getAccountDetails$ = this.accountService
    .getAccountDetails(this.accountId, this.getAccountFilters())
    .pipe(
      catchError(() =>
        throwError('Failed to fetch account details, please reload the page'),
      ),
    );
  public allLicenses$: Observable<LicenseV2[]>;
  public dkimDomains$: Observable<DkimDomain>;
  public emailQuota: EmailQuota;
  public emailMonthlyQuotaIsLoading = false;
  public emailOneTimeQuotaIsLoading = false;
  public upgradeAccountInProgress = false;
  public updateAccountInProgress = false;
  public isDownloadingUserReport = false;

  public accountLicensePackage$ = new BehaviorSubject<string | null>(null);
  public licensesIsLoading$: Subject<boolean> = new Subject<boolean>();
  public editingPackage$: Subject<boolean> = new Subject<boolean>();
  // TODO: account details it's something that's shared across the whole account view and it should be moved to store
  public accountDetails$ = new BehaviorSubject<IAccount | null>(null);

  private _accountSettings$ = new BehaviorSubject<AccountSettings | null>(null);
  public accountSettings$ = this._accountSettings$.asObservable();

  private updateDkimDomains$ = new Subject<DkimDomain>();

  private destroy$: Subject<void> = new Subject<void>();
  private addAsStaffAdminModalRef: NgbModalRef;

  private _allActiveUsers: IAccountUser[] = [];
  private _users: IAccountUser[] = [];
  private _supportUsers: IAccountUser[] = [];
  private _deactivatedUsers: IAccountUser[] = [];

  public usersCount$: Observable<number>;

  public get allActiveUsers() {
    return this._allActiveUsers;
  }

  public set allActiveUsers(users) {
    this._allActiveUsers = users.filter(({ deactivated }) => !deactivated);

    this.isAlreadyOnAccount = this._allActiveUsers.some(
      (user) => user.email === this.auth.profile.email,
    );

    this.users = [...this._allActiveUsers];
    this.supportUsers = [...this._allActiveUsers];

    this.activeUsers$.next(this._allActiveUsers);
  }

  public get users() {
    return this._users;
  }

  public set users(users) {
    this._users = users.filter(
      ({ accountUserType }) => accountUserType === AccountUserType.Customer,
    );
  }

  public get supportUsers() {
    return this._supportUsers;
  }

  public set supportUsers(users) {
    this._supportUsers = users.filter(
      ({ accountUserType }) => accountUserType !== AccountUserType.Customer,
    );
    this.hasSupportUsers$.next(!!this._supportUsers.length);
  }

  public get deactivatedUsers() {
    return this._deactivatedUsers;
  }

  public set deactivatedUsers(users) {
    this._deactivatedUsers = users.filter(({ deactivated }) => deactivated);
    this.hasDeactivatedUsers$.next(!!this._deactivatedUsers.length);
  }

  public set accountDetails(details: IAccount) {
    this._accountDetails = details;
  }

  public get accountDetails() {
    return this._accountDetails;
  }

  public get hasAccountBeenDisabled() {
    return (
      this.accountDetails?.state === AccountState.SuspendPending ||
      this.accountDetails?.state === AccountState.Suspended ||
      this.accountDetails?.state === AccountState.SuspendFailed ||
      this.accountDetails?.state === AccountState.TerminatePending ||
      this.accountDetails?.state === AccountState.Terminated ||
      this.accountDetails?.state === AccountState.TerminateFailed ||
      this.accountDetails?.state === AccountState.DeleteScheduled ||
      this.accountDetails?.state === AccountState.DeletePending ||
      this.accountDetails?.state === AccountState.Deleted ||
      this.accountDetails?.state === AccountState.DeleteFailed ||
      this.accountDetails?.state === AccountState.ResumePending ||
      this.accountDetails?.state === AccountState.ResumeFailed
    );
  }

  public get hasAccountStatusButtonsDisabled() {
    return (
      this.accountDetails?.state === AccountState.DeleteScheduled ||
      this.accountDetails?.state === AccountState.DeletePending ||
      this.accountDetails?.state === AccountState.Deleted
    );
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly modalService: NgbModal,
    private readonly auth: AuthService,
    private readonly toastr: ToastrService,
    private readonly smsCreditsService: SmsCreditsService,
    private readonly proAccountsService: ProAccountsService,
    private readonly accountService: AccountService,
    private readonly userService: UserService,
    private readonly store: Store<State>,
    private readonly routingStore: Store<RoutingState>,
    private readonly pageTitle: Title,
    private readonly router: Router,
    private readonly integrationsService: IntegrationsService,
    private readonly featureToggleStateService: FeatureToggleStateService,
    private readonly userRolesService: UserRolesAndPermissionsService,
    private readonly trialAccountUpgradeService: TrialAccountUpgradeService,
    private readonly dkimService: DkimService,
    @Inject('slimPackage') public slimPackage: string,
  ) {
    this.store.dispatch(new FetchAllLicenses({ accountId: this.accountId }));
  }

  ngOnInit() {
    if (!this.auth.isAuthenticated()) {
      return;
    }

    const getDkimDomains$ = this.accountDetails$.pipe(
      startWith({} as IAccount),
      map((ac) => ({ id: ac?.id, category: ac?.category })),
      pairwise(),
      filter(([prev, next]) => prev?.category !== next.category),
      map(([, { id, category }]) => ({ id, category })),
    );

    this.dkimDomains$ = merge(getDkimDomains$, this.updateDkimDomains$).pipe(
      switchMap((payload) =>
        !!(payload as IAccount).id
          ? this.dkimService.getDomains(
              (payload as IAccount).id,
              (payload as IAccount).category as AccountCategoryCode,
            )
          : this.dkimService
              .updateDomains(
                this.accountDetails.id,
                this.accountDetails.category as AccountCategoryCode,
                payload as DkimDomain,
              )
              .pipe(
                catchError((err) => {
                  this.toastr.error(
                    err?.error?.detail,
                    'Failed to update dkim domains',
                  );
                  return EMPTY;
                }),
              ),
      ),
      shareReplay(1),
    );

    this.allLicenses$ = this.featureToggleStateService.selectLicenses().pipe(
      tap((allLicenses) => (this.allLicenses = allLicenses)),
      shareReplay(1),
    );

    this.disablePackage$ = combineLatest([
      this.featureToggleStateService.selectLicenses(),
      this.store.pipe(select(fromAccountSections.getAccountSectionsTotal)),
    ]).pipe(
      map(
        ([licenses, sectionsTotal]) =>
          !!licenses.some(
            (l) =>
              l.state === AccountState.Failed ||
              l.state === AccountState.Pending,
          ) || !!(sectionsTotal >= 100),
      ),
    );

    this.hasOwnerRole =
      this.auth?.profile?.permissions?.includes(Permission.CanUpdateRole) ||
      false;

    this.hasStaffUserOnAccount$ = this.activeUsers$.pipe(
      map((activeUsers) => {
        const signedInUserOnAccount = activeUsers.find(
          (user) => user.email === this.auth.profile.email,
        );
        return signedInUserOnAccount
          ? signedInUserOnAccount.accountUserType === AccountUserType.Staff
          : false;
      }),
    );

    this.usersCount$ = this.activeUsers$.pipe(
      map(
        (activeUsers) =>
          activeUsers.filter((u) => u.accountUserType !== AccountUserType.Staff)
            .length,
      ),
    );

    this.migrations$ = combineLatest([
      this.store.pipe(select(fromAccount.getAccountMigrations)),
      this.store.pipe(select(fromAccount.getProAccounts)),
    ]).pipe(
      filter(([migrations, _]) => !!migrations),
      map(([migrations, proAccounts]) => {
        const proAccountsLookup = {};
        if (proAccounts) {
          proAccounts.forEach((a) => {
            proAccountsLookup[a.id] = a.name;
          });
        }
        return migrations.map((m) => ({
          ...m,
          proAccountName: proAccountsLookup[m.proAccountId],
        }));
      }),
    );

    forkJoin([
      this.accountService.getAccountUsers(
        this.accountId,
        this.getAccountFilters(),
      ),
      this.getAccountDetails$,
      this.accountService.getLicenseTypes(),
    ])
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$),
        catchError((error) => {
          this.toastr.error(
            'Failed to fetch account details',
            'Request failed',
            { closeButton: true, disableTimeOut: true },
          );
          this.errorResponse = error;

          return EMPTY;
        }),
      )
      .subscribe(([users, accountDetails, licenseTypes]) => {
        this.allActiveUsers = users;
        this.deactivatedUsers = users;
        this.accountDetails = accountDetails;
        this.accountDetails$.next(this.accountDetails);
        this.licenseTypes = licenseTypes;
        this.accountLicensePackage$.next(this.accountDetails.licensePackage);

        this.pageTitle.setTitle(
          `${this.accountDetails.displayName} | One Back Office`,
        );
        const sortedUsersByLastLogin = this.users
          .filter((u) => !!u.lastLogin)
          .sort(
            (a, b) =>
              new Date(b.lastLogin).getTime() - new Date(a.lastLogin).getTime(),
          );
        this.lastUserLoginTime = sortedUsersByLastLogin.length
          ? sortedUsersByLastLogin[0].lastLogin
          : '';
        this.routingStore.dispatch(
          new UpdateRecentRoutes({
            id: 0,
            type: 'Account',
            title: `${this.accountDetails.displayName}`,
            url: location.href,
          }),
        );
      });

    this.store.dispatch(new LoadPermissions());
    this.userPermissions$ = this.store.pipe(
      select(fromPermissions.getUserPermissions),
      tap((userPermissions) =>
        this.hasWawBackOffice$.next(
          userPermissions?.includes(Permission.WawBackOfficePreLaunch),
        ),
      ),
      shareReplay(1),
    );

    this.licensePackages$ = this.store.pipe(
      select(fromLicenses.getLicensePackages),
      tap((licensePackages) => (this.licensePackages = licensePackages)),
      shareReplay(1),
    );

    this.store
      .pipe(select(fromLicenses.getPackagesIsLoaded), takeUntil(this.destroy$))
      .subscribe((loaded: boolean) => {
        if (!loaded) {
          this.store.dispatch(new FetchLicensePackage());
        }
      });

    this.licensePackageUpdateState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ accountPackageState, newPackage }) => {
        if (
          accountPackageState === AccountState.Success &&
          newPackage &&
          newPackage !== this.accountDetails.licensePackage
        ) {
          this.accountDetails = {
            ...this.accountDetails,
            licensePackage: newPackage,
          };
          this.accountLicensePackage$.next(newPackage);
          this.store.dispatch(new ResetUpdateLicensePackage());
        }
      });

    this.initFeatureToggles();
    this.fetchAccountSettings();
    this.fetchProAccountIds();
    this.fetchSmsCredits();
    this.fetchAccountCategories();
    this.fetchEmailQuota();

    this.hasOnlyTrialDispenserRole$ = this.userRolesService.hasOnlyTrialDispenserRole$;
    this.hasOrderManagerRole$ = this.userRolesService.hasOrderManagerRole$;
    this.hasWawRole$ = this.userRolesService.hasWawRole$;

    this.migrations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => this.hasMigrations$.next(!!m.length));

    combineLatest([this.migrations$, this.hasOnlyTrialDispenserRole$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([m, has]) => {
        this.hasMigrations$.next(!!m.length && !has);
      });

    this.store.dispatch(
      new LoadAccountMigrations({ accountId: this.accountId }),
    );

    this.store.dispatch(
      new LoadSections({
        accountId: this.accountId,
        filters: this.getAccountFilters(),
      }),
    );
    this.store.dispatch(new SetAllowPolling(true));

    this.activeTab$.pipe(takeUntil(this.destroy$)).subscribe((id) => {
      if (id === 'integrations') {
        this.integrationsService.getAccountIntegrations(this.accountId);
      }
    });

    this.featureToggleStateService.loadGroups();
    this.featureToggleStateService.loadLicenses(
      this.accountId,
      this.getAccountFilters(),
    );
  }

  ngOnDestroy() {
    this.store.dispatch(new SetAllowPolling(false));
    this.destroy$.next();
    this.destroy$.complete();
    this.toastr.clear();
  }

  get canPlaceOrders() {
    return this.auth.profile.permissions.includes(Permission.CanPlaceOrders);
  }

  fetchAccountSettings() {
    this.isLoadingAccountSettings = true;

    this.accountService
      .getAccountSettings(this.accountId)
      .pipe(
        finalize(() => {
          this.isLoadingAccountSettings = false;
        }),
        catchError(() => {
          this.toastr.error('Failed to fetch account settings');
          return EMPTY;
        }),
      )
      .subscribe((accountSettings) =>
        this._accountSettings$.next(accountSettings),
      );
  }

  fetchSmsCredits() {
    this.isLoadingSmsCredits = true;

    this.smsCreditsService
      .getSmsCredits(this.accountId)
      .pipe(
        tap((smsCredits) => {
          this.currentSmsCredits = smsCredits;
        }),
        finalize(() => {
          this.isLoadingSmsCredits = false;
        }),
        catchError((error) => {
          this.toastr.error('Failed to fetch SMS credits');
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  updateSmsCredits(payload: PatchSmsCreditsPayload) {
    this.isUpdatingSmsCredits = true;

    this.smsCreditsService
      .patchSmsCredits({ accountId: this.accountId, ...payload })
      .pipe(
        tap((updatedSmsCredits) => {
          this.currentSmsCredits = updatedSmsCredits;
          this.toastr.success('SMS credits have been updated');
        }),
        finalize(() => {
          this.isUpdatingSmsCredits = false;
        }),
        catchError((err) => {
          this.toastr.error('Failed to update SMS Credits');
          return EMPTY;
        }),
      )
      .subscribe();
  }

  fetchProAccountIds() {
    this.store.dispatch(new GetProAccountsAction(this.accountId));
  }

  putProSyncAccount(proId: number) {
    this.proAccountsService
      .linkProAccountIds({ proAccountIds: [proId], accountId: this.accountId })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ successful }) => {
        this.store.dispatch(new PutProAccountsSuccessAction(successful));
        if (
          !this.allLicenses.find(({ code }) => code === LicenseCode.ProSync)
            .enabled
        ) {
          this.addFeatureToggleLicense({
            licenseCode: LicenseCode.ProSync,
            addLicense: true,
          });
        }
      });
  }

  proAccountDeleteLink({
    proAccountId,
    proAccountName,
  }: {
    proAccountId: number;
    proAccountName: string;
  }) {
    this.proAccountsService
      .deleteProAccount(this.accountDetails.id, proAccountId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.toastr.error(
            `An error occurred when trying to delete ${proAccountName} from account`,
          );
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.toastr.warning(
          `Link to ${proAccountName} removed from ${this.accountId}.`,
        );
        this.store.dispatch(new UnlinkProAccountSuccessAction(proAccountId));
      });
  }

  inviteUser(userInvitation: UserInvitation): void {
    const modalInstance = this.modalRef.componentInstance;
    modalInstance.errorResponse = null;
    modalInstance.state = FormState.submitting;
    this.userService
      .inviteUser(this.accountId, userInvitation)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          modalInstance.state = FormState.failure;
          modalInstance.errorResponse = error;
          this.toastr.error('Failed to invite user');
          return EMPTY;
        }),
      )
      .subscribe((newUser) => {
        modalInstance.state = FormState.success;
        this.findAndReactivate(newUser);
        this.allActiveUsers = [newUser, ...this.allActiveUsers];

        this.accountDetails = {
          ...this.accountDetails,
          usersCount: this.accountDetails.usersCount + 1,
        };
        this.modalRef.close();
        this.toastr.success('User invited!');
      });
  }

  deleteUserFromAccount({ user, accountId }: IUserDeletionInfo) {
    this.userService
      .deleteUserFromAccount(user.id, accountId)
      .pipe(
        catchError((error) => {
          this.toastr.error('Could not delete user from account');
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.toastr.success('User has been deleted from account!');
        this.modalRef.close();
        this.onUserDeleted(user);
      });
  }

  editAccountSettings(accountSettings: AccountSettingsPayload) {
    if (accountSettings.audiencePackageSize) {
      this.isUpdatingAudiencePackageSize = true;
    } else if (accountSettings.sectionsLimit) {
      this.isUpdatingSectionsLimit = true;
    } else {
      this.isUpdatingUserSeats = true;
    }
    this.accountService
      .patchAccountSettings(accountSettings, this.accountId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isUpdatingAudiencePackageSize = false;
          this.isUpdatingSectionsLimit = false;
          this.isUpdatingUserSeats = false;
        }),
        tap(() => {
          if (accountSettings.userSeats) {
            this.toastr.success('User seats has been updated!');
          } else if (accountSettings.sectionsLimit) {
            this.toastr.success('Number of sections has been updated!');
          } else {
            this.toastr.success('Number of profiles has been updated!');
          }
        }),
        catchError((error) => {
          this.toastr.error('Could not update account settings');
          return EMPTY;
        }),
      )
      .subscribe((as) => this._accountSettings$.next(as));
  }

  confirmAddStaffAdmin(userId: string = null, user: IAccountUser = null) {
    if (userId === null || userId === UserRoles.Admin || (userId && user)) {
      let bodyText = this.addAsStaffUserModalTexts;
      let permissions = [];
      let sections = [];
      let roles = [UserRoles.User, UserRoles.Admin];

      if (userId && userId !== UserRoles.Admin && user) {
        const accountUser = this.allActiveUsers.find(
          (usr) => usr.id === userId,
        );
        permissions = accountUser.toolPermissions
          ? accountUser.toolPermissions
          : [];
        sections = user.sections ? user.sections : [];
        roles = user.roles ? user.roles : [];
        bodyText = this.addAsStaffUserWithUserPermissionsModalTexts(user);
      }

      this.addStaffUser(bodyText, roles, permissions, sections);
    } else if (userId) {
      this.fetchUser(userId);
    }
  }

  addStaffUser(bodyText, roles, permissions, sections) {
    this.addAsStaffAdminModalRef = this.modalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', keyboard: false },
    );

    const { componentInstance } = this.addAsStaffAdminModalRef;

    componentInstance.headerText = `Confirm Add As Staff User`;
    componentInstance.bodyText = bodyText;
    componentInstance.confirmButtonText = 'Confirm';

    componentInstance.confirmationResult.subscribe((hasVerified: boolean) => {
      if (hasVerified) {
        componentInstance.state = FormState.submitting;
        this.userService
          .inviteStaffUser(
            this.accountDetails.id,
            permissions,
            roles,
            sections,
            null,
          )
          .pipe(
            catchError((error) => {
              this.addAsStaffAdminModalRef.close();
              this.toastr.error('Request to add as Staff User failed ');
              return EMPTY;
            }),
          )
          .subscribe((staffUser) => {
            this.findAndReactivate(staffUser);
            this.allActiveUsers = [staffUser, ...this.allActiveUsers];
            componentInstance.state = FormState.success;
            this.toastr.success(
              'You have been added to account as Staff user!',
            );
          });
        this.addAsStaffAdminModalRef.close();
      }
    });
  }

  fetchUser(userId: string) {
    this.userService
      .getUserDetails(this.accountId, userId)
      .pipe(
        catchError((error) => {
          this.toastr.error('Could not copy the users permissions');
          return EMPTY;
        }),
      )
      .subscribe((user) => {
        if (user) {
          this.confirmAddStaffAdmin(userId, user);
        }
      });
  }

  fetchAccountCategories() {
    this.accountCategories$ = combineLatest([
      this.accountService.fetchAccountCategories(),
      this.userPermissions$,
    ]).pipe(
      map(([accountCategories, userPermissions]) => {
        return accountCategories?.filter((category) => {
          switch (category.code) {
            case AccountCategoryCode.SuiteMaxo: {
              return userPermissions?.includes(
                Permission.WawBackOfficePreLaunch,
              );
            }
            default:
              return true;
          }
        });
      }),
      tap((accountCategories) => {
        this.accountCategories = accountCategories;
      }),
      shareReplay(1),
    );
  }

  openInvitePartnerUserModal() {
    this.modalRef = this.modalService.open(InviteSupportUserModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    const { componentInstance } = this.modalRef;
    componentInstance.accountUsers = this.allActiveUsers;
    componentInstance.accountId = this.accountId;
    componentInstance.expireTimeOptions = PartnerUserInviteOptions;
    componentInstance.partnerInvite = true;
    componentInstance.defaultExpire = 840;
    componentInstance.inviteUser.subscribe(
      (userInvitation: StaffUserInvitation) =>
        this.invitePartnerUser(userInvitation),
    );
  }

  openInviteStaffUserModal() {
    this.modalRef = this.modalService.open(InviteSupportUserModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    const { componentInstance } = this.modalRef;
    componentInstance.accountUsers = this.allActiveUsers;
    componentInstance.accountId = this.accountId;
    componentInstance.expireTimeOptions = StaffUserInviteOptions;
    componentInstance.partnerInvite = false;
    componentInstance.defaultExpire = 8;
    componentInstance.inviteUser.subscribe(
      (userInvitation: StaffUserInvitation) =>
        this.inviteStaffUser(userInvitation),
    );
  }

  inviteStaffUser({
    toolsPermissions,
    roles,
    sections,
    userId,
    name,
    expires,
  }: StaffUserInvitation) {
    this.modalRef.componentInstance.state = FormState.submitting;
    this.userService
      .inviteStaffUser(
        this.accountDetails.id,
        toolsPermissions,
        roles,
        sections,
        userId,
        expires,
      )
      .pipe(
        catchError((error) => {
          this.modalRef.close();
          this.toastr.error('Request to invite Staff User failed');
          return EMPTY;
        }),
      )
      .subscribe((staffUser) => {
        this.findAndReactivate(staffUser);
        this.allActiveUsers = [staffUser, ...this.allActiveUsers];
        this.modalRef.componentInstance.state = FormState.success;
        this.toastr.success(name + ' have been added as Staff user!');
        this.modalRef.close();
      });
  }

  invitePartnerUser({ userId, name, expires, roles }: StaffUserInvitation) {
    this.modalRef.componentInstance.state = FormState.submitting;
    this.userService
      .invitePartnerUser(this.accountDetails.id, userId, expires, roles, [], [])
      .pipe(
        catchError((error) => {
          this.modalRef.close();
          this.toastr.error('Request to invite Partner User failed');
          return EMPTY;
        }),
      )
      .subscribe((staffUser) => {
        this.findAndReactivate(staffUser);
        this.allActiveUsers = [staffUser, ...this.allActiveUsers];
        this.modalRef.componentInstance.state = FormState.success;
        this.toastr.success(name + ' have been added as Parnter user!');
        this.modalRef.close();
      });
  }

  openInviteUserModal() {
    this.modalRef = this.modalService.open(InviteUserModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });
    this.modalRef.componentInstance.inviteUser.subscribe(
      (userInvitation: UserInvitation) => this.inviteUser(userInvitation),
    );
  }

  openConfirmDeleteUserModal(user: IAccountUser) {
    this.modalRef = this.modalService.open(DeleteUserModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });
    const componentInstance = this.modalRef.componentInstance;

    componentInstance.user = user;
    componentInstance.accountId = this.accountId;

    componentInstance.deleteUser.subscribe(
      (deleteUserInfo: IUserDeletionInfo) =>
        this.deleteUserFromAccount(deleteUserInfo),
    );
  }

  get accountId() {
    return this.route.snapshot.paramMap.get('id');
  }

  private onUserDeleted(user: IAccountUser) {
    this.allActiveUsers = this.allActiveUsers.filter((u) => u.id !== user.id);
    this.deactivatedUsers.push({ ...user, deactivated: true });
    if (user.accountUserType !== AccountUserType.Staff) {
      this.accountDetails = {
        ...this.accountDetails,
        usersCount: this.accountDetails.usersCount - 1,
      };
    }
  }

  private get addAsStaffUserModalTexts() {
    return `
      <div>
        You are about to add yourself as Staff User <b>with Admin role</b> to the following account:
      </div>
      <div><br>
        <b>Account:</b> ${
          this.accountDetails.displayName
            ? this.accountDetails.displayName
            : this.accountDetails.id
        } <br>
      </div>
      <div><br>
        <h6>Note: You will automatically be removed from the account in 7 days</h6>
      </div>`;
  }

  addAsStaffUserWithUserPermissionsModalTexts(user: IAccountUser) {
    return `
      <div>
        You are about to add yourself as Staff User with the <b>roles and permissions of ${
          user.name
        }</b> to the following account:
      </div>
      <div><br>
        <b>Account:</b> ${
          this.accountDetails.displayName
            ? this.accountDetails.displayName
            : this.accountDetails.id
        } <br>
      </div>
      <div><br>
        <h6>Note: You will automatically be removed from the account in 7 days</h6>
      </div>`;
  }

  findAndReactivate(user: IAccountUser): void {
    const index = this.deactivatedUsers.map((u) => u.email).indexOf(user.email);
    if (index !== -1) {
      this.deactivatedUsers = updateObjectInArray(this.deactivatedUsers, {
        index,
        item: {
          ...user,
          deactivated: false,
          toolPermissions: [],
        },
      });
    }
  }

  checkTokensAndRefresh() {
    if (this.auth.isTokenAboutToExpire()) {
      this.auth.renewTokens();
    }
  }

  changeTab(id) {
    this.router.navigate([], { fragment: id });
  }

  openConfirmUpdateLicensePackage(order: AccountSettingsPayload) {
    this.modalRef = this.modalService.open(ConfirmPackageModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });
    const componentInstance = this.modalRef.componentInstance;

    componentInstance.order = { ...order };
    componentInstance.accountDetails = { ...this.accountDetails };
    componentInstance.licensePackages = [...this.licensePackages];

    componentInstance.confirmationResult.subscribe(() =>
      this.updateLicensePackage(order),
    );
  }

  updateLicensePackage(order: AccountSettingsPayload) {
    this.store.dispatch(
      new UpdateLicensePackage({
        orderId: order.orderId,
        licensePackage: order.licensePackage,
        accountId: this.accountId,
      }),
    );
    this.modalRef.close();
  }

  navigateToMigration({ oneAccountId, proAccountId }) {
    this.router.navigate([`/migrations/${oneAccountId}/${proAccountId}`]);
  }

  deleteAccountSuccess() {
    this.router.navigate(['/au/accounts']);
  }

  public createCrmKeyspace() {
    this.store.dispatch(new CreateCrmKeyspaceAction(this.accountId));
  }

  public rebootstrapLicenses() {
    this.store.dispatch(new RebootstrapLicensesAction(this.accountId));
  }

  public fetchEmailQuota() {
    this.accountService
      .getEmailQuota(this.accountId)
      .pipe(
        tap((emailQuota) => (this.emailQuota = emailQuota)),
        catchError((err) => {
          this.toastr.error(err?.error?.message);
          return EMPTY;
        }),
        take(1),
      )
      .subscribe();
  }

  public addEmailQuota(payload: {
    quota: EmailQuota;
    orderNumber: string;
    type: string;
  }) {
    const { quota, orderNumber, type } = payload;
    if (type === 'monthly') {
      this.emailMonthlyQuotaIsLoading = true;
    } else {
      this.emailOneTimeQuotaIsLoading = true;
    }
    this.accountService
      .addEmailQuota(this.accountId, orderNumber, quota)
      .pipe(
        tap((emailQuota) => (this.emailQuota = emailQuota)),
        catchError((err) => {
          this.toastr.error(err?.error?.message);
          return EMPTY;
        }),
        finalize(() => {
          this.emailMonthlyQuotaIsLoading = false;
          this.emailOneTimeQuotaIsLoading = false;
        }),
      )
      .subscribe();
  }

  accountActivate() {
    const activate$ = this.accountService
      .accountActivate(this.accountId)
      .pipe(
        catchError(() =>
          throwError(`Account ${this.accountId} cannot be activated`),
        ),
      );

    this.accountStateChange(activate$, this.getAccountDetails$).subscribe(
      (accountDetails) => (this.accountDetails = { ...accountDetails }),
      (error) => this.toastr.error(error),
    );
  }

  accountLock() {
    const lock$ = this.accountService
      .accountLock(this.accountId)
      .pipe(
        catchError(() =>
          throwError(`Account ${this.accountId} cannot be locked`),
        ),
      );

    this.accountStateChange(lock$, this.getAccountDetails$).subscribe(
      (accountDetails) => (this.accountDetails = { ...accountDetails }),
      (error) => this.toastr.error(error),
    );
  }

  accountSuspend() {
    const suspend$ = this.accountService
      .accountSuspend(this.accountId)
      .pipe(
        catchError(() =>
          throwError(`Account ${this.accountId} cannot be suspended`),
        ),
      );

    this.accountStateChange(suspend$, this.getAccountDetails$).subscribe(
      (accountDetails) => (this.accountDetails = { ...accountDetails }),
      (error) => this.toastr.error(error),
    );
  }

  accountReactivate() {
    const reactivate$ = this.accountService
      .accountReactivate(this.accountId)
      .pipe(
        catchError(() =>
          throwError(`Account ${this.accountId} cannot be reactivated`),
        ),
      );

    this.accountStateChange(reactivate$, this.getAccountDetails$).subscribe(
      (accountDetails) => (this.accountDetails = { ...accountDetails }),
      (error) => this.toastr.error(error),
    );
  }

  accountTerminate() {
    const terminate$ = this.accountService
      .accountTerminate(this.accountId)
      .pipe(
        catchError(() =>
          throwError(`Account ${this.accountId} cannot be terminated`),
        ),
      );

    this.accountStateChange(terminate$, this.getAccountDetails$).subscribe(
      (accountDetails) => (this.accountDetails = { ...accountDetails }),
      (error) => this.toastr.error(error),
    );
  }

  accountDelete() {
    const delete$ = this.accountService
      .accountDelete(this.accountId)
      .pipe(
        catchError(() =>
          throwError(`Account ${this.accountId} cannot be terminated`),
        ),
      );

    this.accountStateChange(delete$, this.getAccountDetails$).subscribe(
      (accountDetails) => (this.accountDetails = { ...accountDetails }),
      (error) => this.toastr.error(error),
    );
  }

  accountDeleteCancel() {
    const deleteCancel$ = this.accountService
      .accountDeleteCancel(this.accountId)
      .pipe(
        catchError(() =>
          throwError(`Delete cancel cannot be performed for ${this.accountId}`),
        ),
      );

    this.accountStateChange(deleteCancel$, this.getAccountDetails$).subscribe(
      (accountDetails) => (this.accountDetails = { ...accountDetails }),
      (error) => this.toastr.error(error),
    );
  }

  public addFeatureToggleLicense({ licenseCode, addLicense }) {
    if (addLicense) {
      this.featureToggleStateService.addLicense(this.accountId, licenseCode);
      if (licenseCode === LicenseCode.SplitTest) {
        this.store.dispatch(
          new AccountBootstrapEmailEventAction({
            accountId: this.accountId,
            eventDiscriminators: [EmailEventDiscriminator.Split],
          }),
        );
      }
      if (licenseCode === LicenseCode.TransactionalEmail) {
        this.store.dispatch(
          new AccountBootstrapEmailEventAction({
            accountId: this.accountId,
            eventDiscriminators: transactionalEmailEventsList,
          }),
        );
      }
    } else {
      this.featureToggleStateService.removeLicense(this.accountId, licenseCode);
    }
  }

  public upgradeTrial({ orderNumber, subscriptionId, licensePackage }) {
    const { expirationDate, ...accountDetails } = {
      ...this.accountDetails,
      category: AccountCategoryCode.External,
      sbmRef: subscriptionId,
    };

    this.upgradeAccountInProgress = true;

    this.trialAccountUpgradeService
      .upgrade(accountDetails, orderNumber, licensePackage)
      .pipe(
        tap(([updatedAccount, emailQuota, smsCredits, accountSettings]) => {
          this.accountDetails = updatedAccount;
          this.emailQuota = emailQuota;
          this._accountSettings$.next(accountSettings);
          this.currentSmsCredits = smsCredits;
          let licensePackageName = 'Unknown';
          switch (licensePackage) {
            case LicensePackageCode.Lite:
              licensePackageName = 'Lite';
              break;
            case LicensePackageCode.Send:
              licensePackageName = 'Send';
              break;
            case LicensePackageCode.Essential:
              licensePackageName = 'Essential';
              break;
            case LicensePackageCode.Automate:
              licensePackageName = 'Automate';
              break;
            case LicensePackageCode.AllInOne:
              licensePackageName = 'All in one';
              break;
            case LicensePackageCode.Grow:
              licensePackageName = 'Grow';
              break;
          }
          this.toastr.success(
            `Account package successfully upgraded to ${licensePackageName}`,
          );
        }),
        catchError(() => {
          this.toastr.error(
            `Upgrade from trial account to ${licensePackage} failed`,
            'Request failed',
          );
          return EMPTY;
        }),
        finalize(() => (this.upgradeAccountInProgress = false)),
      )
      .subscribe();
  }

  public updateAccount(updatedAccount: IAccount) {
    this.updateAccountInProgress = true;
    this.accountService
      .updateAccountDetails(this.accountId, updatedAccount)
      .pipe(
        catchError(() => {
          this.toastr.error('Could not edit account');
          return EMPTY;
        }),
        finalize(() => (this.updateAccountInProgress = false)),
      )
      .subscribe((accountDetails: IAccount) => {
        this.accountDetails = accountDetails;
        this.accountDetails$.next(accountDetails);
        this.toastr.success('Account has been updated!');
      });
  }

  public updateDkimDomains(updatedDkimDomain: DkimDomain) {
    this.updateDkimDomains$.next(updatedDkimDomain);
  }

  public downloadUserReport() {
    this.isDownloadingUserReport = true;
    this.accountService
      .downloadUserReport(this.accountId)
      .pipe(
        finalize(() => {
          this.isDownloadingUserReport = false;
        }),
      )
      .subscribe(
        (data: ArrayBuffer) => {
          const filename = `user-report-${this.accountId}-${moment().format(
            'YYYY-MM-DD',
          )}.xlsx`;
          fileDownload(data, filename);
        },
        (error) => {
          this.toastr.error(
            'Failed to download user report. Please try again.',
          );
        },
      );
  }

  private accountStateChange(
    obs1$: Observable<unknown>,
    obs2$: Observable<IAccount>,
  ) {
    this.isLoading = true;
    return obs1$.pipe(
      switchMap(() => obs2$),
      finalize(() => (this.isLoading = false)),
      take(1),
    );
  }

  private getAccountFilters() {
    let filters = {};
    const isAccountDeleted = this.route.snapshot.queryParamMap.get('deleted');
    if (isAccountDeleted) {
      filters = { deleted: true };
    }
    return filters;
  }

  private initFeatureToggles() {
    this.featureTogglesLoading$ = combineLatest([
      this.featureToggleStateService.selectGroupsIsLoading(),
      this.featureToggleStateService.selectLicesesIsLoading(),
    ]).pipe(
      map(([groupLoading, licenseLoading]) => groupLoading || licenseLoading),
      shareReplay(1),
    );

    this.featureToggleGroupNode$ = combineLatest([
      this.featureToggleStateService.selectGroups(),
      this.featureToggleStateService.selectLicenses(),
      this.licensePackages$,
      this.accountLicensePackage$,
    ]).pipe(
      filter(
        ([groups, licenses, packages, accountLicensePackage]) =>
          !!groups?.length &&
          !!licenses?.length &&
          !!packages?.length &&
          !!accountLicensePackage,
      ),
      map(([groups, licenses, packages, accountLicensePackage]) => {
        const packagesLookup = packages.reduce((a, b) => {
          a[b.code] = b.licenses;
          return a;
        }, {});
        const licensePackageCodeName =
          packages.find((pac) => pac.code === accountLicensePackage)
            ?.displayName ?? accountLicensePackage;
        return groups?.map((group) => ({
          name: group.displayName,
          licenses: licenses
            .filter((l) => l.group === group.code)
            .map((license: LicenseV2) => ({
              ...license,
              disabled:
                license.disabled ||
                packagesLookup[accountLicensePackage].includes(license.code),
              disabledForPackage: packagesLookup[
                accountLicensePackage
              ].includes(license.code)
                ? licensePackageCodeName
                : null,
            })),
        }));
      }),
    );
  }
}
